import { ClipboardEvent, forwardRef, KeyboardEvent, useEffect, useImperativeHandle, useRef } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import styles from "./CodeInput.module.scss";

export interface CodeInputHandle {
  focus: () => void;
}

interface CodeInputProps {
  values: string[]; // one digit per box, "" when empty
  onChange: (values: string[]) => void;
  onComplete?: (code: string) => void; // called when every box has a digit
  ariaLabel: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  error?: boolean;
}

// Row of one-digit boxes: auto-advance, backspace goes back, paste / autofill fills all boxes
const CodeInput = forwardRef<CodeInputHandle, CodeInputProps>(function CodeInput(
  { values, onChange, onComplete, ariaLabel, autoFocus = false, readOnly = false, error = false },
  ref
) {
  const { t } = useTranslation();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const length = values.length;

  const focusInput = (index: number) => {
    const input = inputsRef.current[Math.max(0, Math.min(index, length - 1))];
    if (input) {
      input.focus();
      input.select();
    }
  };

  useImperativeHandle(ref, () => ({ focus: () => focusInput(0) }));

  useEffect(() => {
    if (autoFocus) focusInput(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fillFrom = (start: number, digits: string) => {
    const next = [...values];
    const count = Math.min(digits.length, length - start);
    for (let i = 0; i < count; i++) {
      next[start + i] = digits.charAt(i);
    }
    onChange(next);
    focusInput(start + count);
    if (next.every((value) => value !== "")) {
      onComplete?.(next.join(""));
    }
  };

  const handleChange = (rawValue: string, index: number) => {
    if (readOnly) return;

    if (rawValue === "") {
      const next = [...values];
      next[index] = "";
      onChange(next);
      return;
    }

    let digits = rawValue.replace(/\D/g, "");
    if (digits.length >= length) {
      // The whole code at once (autofill from the email / SMS keyboard suggestion)
      fillFrom(0, digits.slice(0, length));
      return;
    }
    if (values[index] && rawValue.length === 2) {
      // A digit was typed next to the one already in this box: keep only the new one
      const newCharIndex = rawValue.indexOf(values[index]) === 0 ? 1 : 0;
      digits = rawValue.charAt(newCharIndex).replace(/\D/g, "");
    }
    if (!digits) return;

    fillFrom(index, digits);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>, index: number) => {
    if (readOnly) {
      e.preventDefault();
      return;
    }

    const text: string | undefined = e.clipboardData?.getData("text");
    if (text === undefined) return; // no clipboard data: the pasted value arrives through onChange
    e.preventDefault();

    const digits = text.replace(/\D/g, "");
    if (!digits) return;

    if (digits.length >= length) {
      fillFrom(0, digits.slice(0, length));
    } else {
      fillFrom(index, digits);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      if (readOnly) return;
      e.preventDefault();
      const next = [...values];
      if (values[index]) {
        next[index] = "";
        onChange(next);
      } else if (index > 0) {
        next[index - 1] = "";
        onChange(next);
        focusInput(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  return (
    <div className={styles.root} role="group" aria-label={ariaLabel}>
      {values.map((value, index) => (
        <input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el;
          }}
          className={cx(styles.root_digit, error && styles.root_digit_error)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          value={value}
          readOnly={readOnly}
          aria-label={t("Digit {{number}} of {{total}}", { number: index + 1, total: length })}
          aria-invalid={error || undefined}
          onChange={(e) => handleChange(e.target.value, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
          onFocus={(e) => e.target.select()}
          onClick={(e) => e.currentTarget.select()}
        />
      ))}
    </div>
  );
});

export default CodeInput;
